/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { INosImageConfig, INosImageProps } from './nos-image.props.autogenerated';

/**
 * The interface for IImageViewProps.
 */
export interface IImageViewProps extends INosImageProps<INosImageConfig> {
    image: IModuleProps;
    paragraph: JSX.Element | undefined;
    imageItem: React.ReactNode | undefined;
}

/**
 *
 * Image component.
 * @extends {React.PureComponent<INosImageProps<INosImageConfig>>}
 */
class Image extends React.PureComponent<INosImageProps<INosImageConfig>> {
    public render(): JSX.Element | null {
        const viewProps: IImageViewProps = {
            ...this.props,
            image: {
                moduleProps: this.props,
                className: classnames('nos-image', this.props.config.className),
                tag: 'div'
            },
            paragraph: this._renderParagraph(),
            imageItem: this._renderImageItem()
        };
        return this.props.renderView(viewProps);
    }
    public handleParagraphChange = () => (event: Msdyn365.ContentEditableEvent): void => {
        if (this.props.config.image) {
            this.props.config.paragraph = event.target.value;
        }
    };

    private readonly _renderParagraph = () => {
        const paragraph = this.props.config.paragraph;

        const paragraphText = (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className='nos-image__text'
                editProps={{ onEdit: this.handleParagraphChange(), requestContext: this.props.context.request }}
            />
        );

        return paragraphText;
    };

    /**
     * Render image.
     * @returns Image with optional text (caption).
     */
    private readonly _renderImageItem = () => {
        const image = this.props.config.image;
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=360&h=270&q=80&m=6', w: 360, h: 270 },
                sm: { q: 'w=560&h=420&q=80&m=6', w: 560, h: 420 },
                md: { q: 'w=560&h=570&q=80&m=6', w: 760, h: 570 },
                lg: { q: 'w=420&h=315&q=80&m=6', w: 420, h: 315 },
                xl: { q: 'w=600&h=450&q=80&m=6', w: 600, h: 450 }
            },
            lazyload: true,
            disableLazyLoad: false
        };

        const imageProps = {
            gridSettings: this.props.context.request.gridSettings!,
            imageSettings: (image?.imageSettings ?? defaultImageSettings) as Msdyn365.IImageSettings | undefined
        };

        // If image is null, we want to render the placeholder SVG - by passing image data with empty src
        const imageItem = (
            <Msdyn365.Image {...image} {...imageProps} loadFailureBehavior='empty' requestContext={this.props.context.request} />
        );

        return imageItem;
    };
}

export default Image;
